import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';
import omitBy from 'lodash.omitby';

import getConfig from '../../util/config';

const useGtagTracking = () => {
  const { google } = getConfig();
  const initialLocation = useLocation();
  const lastLocationRef = useRef(initialLocation);
  const { replace, listen } = useHistory();
  const { gtag } = global;

  //  Initial setup + Tracks the initial landing location
  useEffect(() => {
    if (gtag) {
      gtag('js', new Date());
      gtag('config', google.gtagId);
      gtag('event', 'page_view', {
        page_path: initialLocation.pathname,
      });
    }
  }, [gtag]);

  useEffect(() => {
    return listen(location => {
      const lastLocation = lastLocationRef?.current;
      const { pathname: lastPathname, search: lastSearch } = lastLocation;
      const { pathname: newPathname, search: newSearch } = location;
      const {
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        _gl: utmGl,
        _ga: utmGa,
      } = qs.parse(lastSearch ?? '');
      const {
        utm_source: newUtmSource,
        utm_campaign: newUtmCampaign,
        utm_medium: newUtmMedium,
        _gl: newGl,
        _ga: newGa,
      } = qs.parse(newSearch ?? '');

      if (lastPathname === newPathname) {
        return;
      }

      if (gtag) {
        gtag('js', new Date());
        gtag('event', 'page_view', {
          page_path: newPathname,
        });
      }

      // There is no real objective to replacing / updating the URL if no relevant utm tag
      // data is available. Therefore, check any of these for value and skip if all are empty.
      if (!utmSource && !utmCampaign && !utmMedium && !utmGl && !utmGa) {
        return;
      }

      // In the same order as above, we would only create additional loops if we keep trying to
      // replace information on a place we already have it.
      if (
        utmSource === newUtmSource &&
        utmCampaign === newUtmCampaign &&
        utmMedium === newUtmMedium &&
        utmGl === newGl &&
        utmGa === newGa
      ) {
        return;
      }

      const parsedNewSearch = qs.parse(newSearch);

      const utmParameters = {
        utm_source: utmSource,
        utm_campaign: utmCampaign,
        utm_medium: utmMedium,
        _gl: utmGl,
        _ga: utmGa,
      };

      const finalLocation = {
        ...location,
        search: qs.stringify({
          ...omitBy(utmParameters, x => !x),
          ...parsedNewSearch,
        }),
      };

      lastLocationRef.current = finalLocation;

      replace(finalLocation);
    });
  }, [gtag, listen]);
};

export default useGtagTracking;
